import { type CarouselSection, type Link } from '@/features/cms/components/CMS/types'
import { classNames } from '@/features/shared/utils/classNames'
import * as Accordion from '@radix-ui/react-accordion'
import { Stack } from '@shipt/design-system-layouts'
import { screenSizes } from '@shipt/design-system-themes'
import { Body, Headline } from '@shipt/design-system-typography'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { TextButtonLink } from '@/features/shared/elements/Link/Buttons'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { AnimatePresence, m } from 'framer-motion'
import { theme } from '@/features/shared/constants/shiptTheme'

type Props = {
  sections: CarouselSection[]
  delay: number
  disableAutoPlay: boolean
  imagePlacement: 'left' | 'right'
}

export const DesktopCarousel = ({ sections, delay, imagePlacement, disableAutoPlay }: Props) => {
  const [isPaused, setIsPaused] = useState(false)
  const [currentSection, setCurrentSection] = useState(0)
  const { trackCMSElementClicked } = useCMSTrackingEvents()

  useEffect(() => {
    if (!isPaused && !disableAutoPlay) {
      const timeout = setInterval(() => {
        setCurrentSection((prevValue) => {
          if (prevValue === sections.length - 1) {
            return 0
          }
          return prevValue + 1
        })
      }, delay)

      return () => clearInterval(timeout)
    }
  }, [delay, sections.length, isPaused, disableAutoPlay])

  const handleClick = (index: number) => {
    setCurrentSection(index)
    setIsPaused(true)
  }

  const handleOnClick = (link: Link) => {
    trackCMSElementClicked({
      type: 'link',
      next_location: link.url,
      content: link.text,
    })
  }

  const flexDirection = imagePlacement === 'left' ? 'row-reverse' : 'row'

  return (
    <Container direction={flexDirection} justify="center" align="center">
      <AccordionRoot type="single" value={`item-${currentSection}`} collapsible>
        {sections.map(({ data: { heading, subheading_rich_text, link, image } }, index) => {
          const richTextElements = subheading_rich_text?.elements ?? []
          return (
            <Item value={`item-${index}`} key={heading + image.src}>
              <Trigger onClick={() => handleClick(index)}>
                <Heading>{heading}</Heading>
              </Trigger>
              <AnimatePresence initial={false}>
                {currentSection === index && (
                  <m.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Accordion.Content>
                      <m.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.7 }}
                      >
                        {richTextElements.length > 0 && (
                          <Text variant="secondary" as="div">
                            {richTextElements.map(renderElement)}
                          </Text>
                        )}
                        {!!link && (
                          <TextButtonLink href={link.url} onClick={() => handleOnClick(link)}>
                            {link.text}
                          </TextButtonLink>
                        )}
                      </m.div>
                    </Accordion.Content>
                  </m.div>
                )}
              </AnimatePresence>
            </Item>
          )
        })}
      </AccordionRoot>
      <ImageContainer>
        {sections.map(({ data: { heading, image } }, index) => (
          <ImageWrapper
            className={classNames(currentSection === index && 'show')}
            key={heading + image.src}
            src={image.src}
            alt={image.alt}
            height={image.height}
            width={image.width}
          />
        ))}
      </ImageContainer>
    </Container>
  )
}

const Container = styled(Stack)`
  gap: 1.25rem;
  padding-left: 40px;
  align-items: flex-start;

  @media ${screenSizes.smDesktop} {
    gap: 2.5rem;
  }

  @media ${screenSizes.lgDesktop} {
    padding: 0 100px;
    gap: 2.5rem;
  }
`

const AccordionRoot = styled(Accordion.Root)`
  width: 50%;
`

const Item = styled(Accordion.Item)`
  display: flex;
  flex-direction: column;
`

const Heading = styled(Headline)`
  color: #6b6471;
`

const Trigger = styled(Accordion.Trigger)`
  border: none;
  background: none;
  padding: 0.75rem 0 0.5rem;
  cursor: pointer;
  text-align: left;
  position: relative;
  overflow: visible;

  &::before {
    content: '';
    display: block;
    background-color: ${theme.green};
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 100%;
    margin-right: 0.5rem;
    transition: all 300ms cubic-bezier(0.445, 0.05, 0.55, 0.95);
    opacity: 0;
    position: absolute;
    left: -1.25rem;
    top: 1.5rem;
  }

  &[data-state='open'] {
    ${Heading} {
      color: #302638;
    }

    &::before {
      opacity: 1;
    }
  }
`

const Text = styled(Body)`
  font-size: 0.875rem;
  padding-bottom: 8px;

  @media ${screenSizes.smDesktop} {
    font-size: 1rem;
  }
`

const ImageContainer = styled.div`
  position: relative;
  width: 50%;
  aspect-ratio: 10.5 / 7;
  height: 100%;
`

const ImageWrapper = styled(Image)`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 300ms cubic-bezier(0.445, 0.05, 0.55, 0.95);

  &.show {
    opacity: 1;
  }
`
